
import { cn } from '@/utils/cn';
import type { LeagueParticipant } from '../services/leagueService';

interface LeaguePodiumProps {
    topThree: LeagueParticipant[];
    remainingHours?: number;
}

export const LeaguePodium = ({ topThree }: LeaguePodiumProps) => {
    // Ensure we have 3 slots, filled with null if missing
    const podiumData = [
        topThree.find(p => p.rank === 2), // Left
        topThree.find(p => p.rank === 1), // Center (Winner)
        topThree.find(p => p.rank === 3), // Right
    ];

    const toPersianDigits = (v: number | string | undefined | null) =>
        (v !== undefined && v !== null) ? String(v).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]) : '';

    return (
        <div className="relative px-4">
            <div className="flex justify-center items-end gap-2 min-h-[200px]">
                {podiumData.map((participant, index) => {
                    if (!participant) {
                        // Placeholder for empty slot to maintain layout
                        return <div key={`empty-${index}`} className="w-1/3" />;
                    }

                    const isFirst = participant.rank === 1;
                    const isSecond = participant.rank === 2;

                    return (
                        <div
                            key={participant.participant_id}
                            className={cn(
                                "flex flex-col items-center relative z-10",
                                isFirst ? "w-1/3 -mb-1" : "w-1/4" // First place is slightly wider and sits lower/higher visually
                            )}
                        >
                            <div className="relative mb-2">
                                <div className={cn(
                                    "rounded-full border-4 border-white/20 overflow-hidden shadow-lg",
                                    isFirst ? "w-[72px] h-[72px] md:w-[86px] md:h-[86px]" : "w-[50px] h-[50px] md:w-[58px] md:h-[58px]"
                                )}>
                                    {participant.avatar_url ? (
                                        <img
                                            src={participant.avatar_url}
                                            alt={participant.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-primary-200 flex items-center justify-center text-primary-700 text-xl">
                                            {participant.name.charAt(0)}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Name and Score */}
                            <div className="text-center mb-1 flex flex-col items-center">
                                <div className={cn(
                                    "font-bold text-white truncate max-w-[90px] font-persian leading-tight",
                                    isFirst ? "text-sm" : "text-[11px]"
                                )}>
                                    {participant.name}
                                </div>
                                {/* Score Display */}
                                <div className={cn(
                                    "font-medium text-white/80 font-persian mt-0.5 flex items-center justify-center gap-1",
                                    isFirst ? "text-xs" : "text-[10px]"
                                )}>
                                    <span>{toPersianDigits(participant.points_in_cycle ?? 0)}</span>
                                    <span className="text-[9px]">امتیاز</span>
                                </div>
                            </div>

                            {/* Pedestal */}
                            <div className={cn(
                                "w-full rounded-t-lg flex items-center justify-center text-white/90 font-bold text-3xl shadow-lg backdrop-blur-sm font-persian",
                                isFirst ? "h-[85px] bg-gradient-to-b from-white/20 to-white/5" :
                                    isSecond ? "h-[65px] bg-gradient-to-b from-white/15 to-white/5" :
                                        "h-[45px] bg-gradient-to-b from-white/10 to-white/5"
                            )}>
                                {toPersianDigits(participant.rank)}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
